import * as React from "react";

import { getInverseText, getTone } from "@/lib/ui/tones";
import { cn } from "@/lib/utils";

/**
 * The gray section eyebrow — the small tracked-caps label that sits above a
 * group of fields, a list, a stat or a definition ("Details", "Assigned to",
 * "Studio library"). It is the "gray eyebrow" of docs/design-guidelines.md
 * §4 "Admin page header": `text-[11px] font-semibold uppercase
 * tracking-[0.18em] text-gray-500`.
 *
 * Before this the same label was written ~130 times with five trackings, two
 * greys and two sizes. `tests/unit/sharedRecipes.test.jsx` fails the build on
 * a hand-rolled copy. `as` picks the element (`p` by default; `h2`…`h4`, `dt`,
 * `label`, `span`, `div`), `className` adds layout only (`mb-2`, `flex
 * items-center gap-2`).
 *
 * The same label in a hue — a type of thing told apart by colour, like Guide's
 * cue types — takes `tone` (a hue from `lib/ui/tones.js`); on the navy
 * surface (`bg-surface-inverse`) it takes `inverse`, which reads the hue's
 * on-dark slot (neutral's without a tone). Size and tracking never change.
 *
 * Not for: a label above a page's H1 (page headers carry no eyebrow — the
 * title stands alone), the 10px nav/sidebar group labels, table header
 * cells, or coloured tag pills.
 */
const EYEBROW_TYPE = "text-[11px] font-semibold uppercase tracking-[0.18em]";
const EYEBROW_CLASS = `${EYEBROW_TYPE} text-gray-500`;

function eyebrowColor(tone, inverse) {
  if (inverse) return getInverseText(tone);
  return tone ? getTone(tone).icon : "text-gray-500";
}

/**
 * @param {object} props
 * @param {string} [props.as] the element (`p` by default).
 * @param {string} [props.tone] a hue from `lib/ui/tones.js`; gray without one.
 * @param {boolean} [props.inverse] drawn on the navy surface.
 * @param {string} [props.className] layout only.
 */
const Eyebrow = React.forwardRef(
  ({ as: Comp = "p", tone, inverse = false, className, ...props }, ref) => (
    <Comp
      ref={ref}
      className={cn(EYEBROW_TYPE, eyebrowColor(tone, inverse), className)}
      {...props}
    />
  )
);
Eyebrow.displayName = "Eyebrow";

export { Eyebrow, EYEBROW_CLASS };
